import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { onAuthStateChanged } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import { auth } from '../../firebase';
import axios from '../../utils/axios';
import config from '../../config/env.js';
import { showAlert } from './alertSlice';
import { createAuthedThunk } from '../authedThunk';

const initialState = {
  streaming: {
    active: false,
    content: '',
    thinkingMessage: '',
    requestId: null,
  },
  allConversationsMain: {
    fetching: null,
    fetchingPagination: null,
    fetchingSuccessful: null,
    fetchingFailed: null,
    error: null,
    errorText: null,
    conversations: null,
    emptyList: null,
    lastSeen: null,
  },
  conversationDetailsById: {
    fetching: null,
    fetchingSuccessful: null,
    fetchingFailed: null,
    error: null,
    errorText: null,
    details: null,
  },
  messageResponse: {
    fetching: false,
    fetchingSuccessful: false,
    fetchingFailed: false,
    error: null,
    response: null,
  },
  messageResponseAsync: {
    fetching: false,
    fetchingSuccessful: false,
    fetchingFailed: false,
    error: null,
    response: null,
  },
  // Candidate actions
  createConversation: {
    processing: null,
    done: null,
    failed: null,
    error: null,
  },
  deleteConversation: {
    processing: null,
    done: null,
    failed: null,
    error: null,
  },
  allConversations: {
    fetching: null,
    fetchingSuccessful: null,
    fetchingFailed: null,
    data: null,
  },
  conversationsByUserId: {
    fetching: null,
    fetchingSuccessful: null,
    fetchingFailed: null,
    data: null,
  },
  updateConversationStatusState: {
    processing: null,
    done: null,
    failed: null,
    error: null,
  },
};

// Standalone action so the thunk (defined here) and the slice (defined below)
// can both reference it without a circular dependency.
export const streamChunkReceived = createAction(
  'conversations/streamChunkReceived',
);

export const streamThinkingReceived = createAction(
  'conversations/streamThinkingReceived',
);

export const streamRequestIdReceived = createAction(
  'conversations/streamRequestIdReceived',
);

export const streamMessageResponse = createAsyncThunk(
  'conversations/streamMessageResponse',
  async (
    { conversationId, message, signal, discardOnStop },
    { dispatch, rejectWithValue },
  ) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            let fullResponse = '';
            const clientRequestId = uuidv4();

            const response = await fetch(`${config.apiBaseUrl}go-ai/stream`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Request-ID': clientRequestId,
              },
              body: JSON.stringify({ conversationId, message, discardOnStop }),
              signal,
            });

            if (!response.ok) {
              throw new Error(`HTTP error ${response.status}`);
            }

            // The server may echo back the same ID or assign its own; this
            // is the ID that must be sent to /go-ai/stop to cancel the run.
            dispatch(
              streamRequestIdReceived(
                response.headers.get('X-Request-ID') || clientRequestId,
              ),
            );

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            for (;;) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });

              // Split on SSE block boundaries; keep the trailing incomplete block.
              const blocks = buffer.split('\n\n');
              buffer = blocks.pop();

              for (const block of blocks) {
                const dataLines = block
                  .split('\n')
                  .filter((l) => l.startsWith('data:'));
                if (!dataLines.length) continue;

                const rawData = dataLines
                  .map((l) => {
                    const raw = l.slice(5); // remove "data:"
                    return raw.startsWith(' ') ? raw.slice(1) : raw;
                  })
                  .join('\n');

                const data = rawData.trim();
                if (data === '[DONE]') continue;

                let chunk = '';
                try {
                  const parsed = JSON.parse(data);
                  chunk =
                    parsed.text ??
                    parsed.content ??
                    parsed.delta?.text ??
                    parsed.delta?.content ??
                    parsed.choices?.[0]?.delta?.content ??
                    parsed.choices?.[0]?.delta?.text ??
                    (typeof parsed.delta === 'string' ? parsed.delta : '') ??
                    '';
                } catch {
                  chunk = rawData;
                }
                if (chunk) {
                  if (chunk.startsWith('thinking:')) {
                    const thinkingMsg = chunk.slice('thinking:'.length).trim();
                    if (thinkingMsg)
                      dispatch(streamThinkingReceived(thinkingMsg));
                  } else {
                    fullResponse += chunk;
                    dispatch(streamChunkReceived(chunk));
                  }
                }
              }
            }

            resolve(fullResponse);
          } catch (e) {
            if (e?.name === 'AbortError') {
              reject(rejectWithValue('AbortError'));
            } else {
              reject(rejectWithValue(e?.message || 'Stream failed'));
            }
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

// Cancels an in-flight /go-ai/stream call on the backend so it stops
// generating and skips persisting the discarded response. A 404 means the
// stream already finished server-side, which is a safe no-op.
export const stopStreamThunk = createAsyncThunk(
  'conversations/stopStream',
  async ({ requestId }, { rejectWithValue }) => {
    if (!requestId) return null;
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            await axios.post('/go-ai/stop', null, {
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Request-ID': requestId,
              },
            });
            resolve(null);
          } catch (e) {
            if (e?.response?.status === 404) return resolve(null);
            reject(rejectWithValue(e?.response?.data || e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

// Shared auth wrapper - see `redux/authedThunk`. Was a byte-identical
// local copy in each of eleven slices.
const thunk = createAuthedThunk;

export const createNewConversationThunk = thunk(
  'conversations/createNewConversation',
  async (token, { signal, ...payload }) => {
    const r = await axios.post('/cs/conversation', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to create conversation');
  },
);

export const deleteConversationThunk = thunk(
  'conversations/deleteConversation',
  async (token, conversationId) => {
    const r = await axios.delete(`/cs/conversation/${conversationId}`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to delete conversation');
  },
);

export const fetchAllConversations = thunk(
  'conversations/fetchAllConversations',
  async (token) => {
    const r = await axios.get('/cs/conversation', {
      params: { 'request-id': uuidv4(), lastSeen: '', limit: 100 },
      headers: {
        Authorization: `Bearer ${token}`,
        'current-role': 'candidate',
      },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch conversations');
  },
);

export const fetchConversationsByUserId = thunk(
  'conversations/fetchConversationsByUserId',
  async (token, { teamId, uId, size, lastSeen }) => {
    const r = await axios.get(`/cs/users/${uId}/conversation`, {
      params: { 'request-id': uuidv4(), lastSeen, limit: size, teamId },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch conversations by user');
  },
);

export const updateConversationStatusThunk = thunk(
  'conversations/updateConversationStatus',
  async (token, { conversationId, status, signal }) => {
    const r = await axios.put(
      `/cs/conversation/${conversationId}/status`,
      null,
      {
        params: { 'request-id': uuidv4(), status },
        headers: { Authorization: `Bearer ${token}` },
        signal,
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to update conversation status');
  },
);

export const fetchConversationsMain = createAsyncThunk(
  'conversations/fetchConversationsMain',
  async (
    { botId, dealId, teamId, conversationType, size, lastSeen, currentRole },
    { dispatch, rejectWithValue },
  ) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const headers = { Authorization: `Bearer ${token}` };
            if (currentRole) {
              headers['current-role'] = currentRole;
            }
            const response = await axios.get(`/cs/conversation`, {
              params: {
                'request-id': reqId,
                lastSeen: lastSeen || '',
                conversationType,
                limit: size || 100,
                teamId,
                botId,
                dealId,
              },
              headers,
            });
            if (response.status === 200) {
              resolve({
                data: response.data,
                lastSeenArg: lastSeen,
                responseDataLastSeen:
                  response?.data?.[response?.data?.length - 1]?.id,
              });
            } else {
              dispatch(
                showAlert({
                  message: 'Oops! Something went wrong. Please try again.',
                  showCross: true,
                  title: null,
                  type: 'error',
                  autoHideDuration: 2000,
                  vertical: 'top',
                  horizontal: 'center',
                }),
              );
              reject(rejectWithValue('Failed to get conversations'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Oops! Something went wrong. Please try again.',
                showCross: true,
                title: null,
                type: 'error',
                autoHideDuration: 2000,
                vertical: 'top',
                horizontal: 'center',
              }),
            );
            reject(rejectWithValue('Failed to get conversations'));
          }
        },
        (error) => {
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const fetchConversationById = createAsyncThunk(
  'conversations/fetchConversationById',
  async (
    { conversationId, speaker, setmanually, signal },
    { rejectWithValue },
  ) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.get(
              `/cs/conversation/${conversationId}`,
              {
                params: { 'request-id': reqId, speaker },
                headers: {
                  Authorization: `Bearer ${token}`,
                  'x-api-version': 'v2',
                },
                signal,
              },
            );
            if (response.status === 200) {
              resolve({
                conversationId,
                data: response.data,
                setmanually,
              });
            } else {
              reject(rejectWithValue('Failed to get conversation by id'));
            }
          } catch (e) {
            reject(rejectWithValue('Failed to get conversation by id'));
          }
        },
        (error) => {
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const getMessageResponse = createAsyncThunk(
  'conversations/getMessageResponse',
  async (
    {
      conversationId,
      message,
      speaker,
      status,
      videoId,
      version,
      wordsLimit,
      dealId,
      callback,
      errCb,
    },
    { rejectWithValue },
  ) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            if (typeof errCb === 'function') errCb('User not authenticated');
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.post(
              `/cs/conversation/${conversationId}`,
              {
                message,
                status: status || null,
                speaker,
                videoId,
                dealId,
                wordsLimit,
              },
              {
                params: {
                  'request-id': reqId,
                },
                headers: {
                  Authorization: `Bearer ${token}`,
                  'x-api-version': version || 'v1',
                },
              },
            );
            if (response.status === 200) {
              if (typeof callback === 'function') callback(response?.data);
              resolve(response?.data);
            } else {
              if (typeof errCb === 'function')
                errCb(response?.response?.data?.message);
              reject(rejectWithValue('Failed to get gpt response'));
            }
          } catch (e) {
            if (typeof errCb === 'function') errCb();
            reject(rejectWithValue('Failed to get gpt response'));
          }
        },
        (error) => {
          if (typeof errCb === 'function') errCb();
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const getMessageResponseAsync = createAsyncThunk(
  'conversations/getMessageResponseAsync',
  async (
    {
      conversationId,
      message,
      speaker,
      status,
      videoId,
      version,
      wordsLimit,
      dealId,
      callback,
      errCb,
    },
    { rejectWithValue },
  ) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) {
            if (typeof errCb === 'function') errCb('User not authenticated');
            return reject(rejectWithValue('User not authenticated'));
          }
          try {
            const token = await user.getIdToken();
            const reqId = uuidv4();
            const response = await axios.post(
              `/cs/conversation/${conversationId}/async`,
              {
                message,
                status: status || null,
                speaker,
                videoId,
                dealId,
                wordsLimit,
              },
              {
                params: {
                  'request-id': reqId,
                },
                headers: {
                  Authorization: `Bearer ${token}`,
                  'x-api-version': version || 'v1',
                },
              },
            );
            if (response.status === 200) {
              if (typeof callback === 'function') callback(response?.data);
              resolve(response?.data);
            } else {
              if (typeof errCb === 'function')
                errCb(response?.response?.data?.message);
              reject(rejectWithValue('Failed to get gpt response'));
            }
          } catch (e) {
            if (typeof errCb === 'function') errCb();
            reject(rejectWithValue('Failed to get gpt response'));
          }
        },
        (error) => {
          if (typeof errCb === 'function') errCb();
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    updateAllConversationsAfterDelete: (state, action) => {
      const { id } = action.payload;
      if (state.allConversationsMain.conversations) {
        state.allConversationsMain.conversations = state.allConversationsMain.conversations.filter(
          (cs) => cs.id !== id,
        );
      }
    },
    updateConversationsAfterAnalysis: (state, action) => {
      const { id, data } = action.payload;
      if (state.allConversationsMain.conversations) {
        state.allConversationsMain.conversations = state.allConversationsMain.conversations.map(
          (cs) => (cs?.id === id ? data : cs),
        );
      }
    },
    updateConversationByKey: (state, action) => {
      const { id, key, value } = action.payload;
      if (state.allConversationsMain.conversations) {
        state.allConversationsMain.conversations = state.allConversationsMain.conversations.map(
          (cs) => (cs?.id === id ? { ...cs, [key]: value } : cs),
        );
      }
    },
    updatePracticeCallAnalysisScores: (state, action) => {
      const { id, scoreKey, scoreValue } = action.payload;
      if (state.allConversationsMain.conversations) {
        state.allConversationsMain.conversations = state.allConversationsMain.conversations.map(
          (cs) =>
            cs?.id === id
              ? {
                  ...cs,
                  analysis: {
                    ...cs.analysis,
                    [`${scoreKey}Score`]: scoreValue,
                  },
                }
              : cs,
        );
      }
    },
    updatePracticeCallAnalysisScoreStatus: (state, action) => {
      const { id, scoreStatus } = action.payload;
      if (state.allConversationsMain.conversations) {
        state.allConversationsMain.conversations = state.allConversationsMain.conversations.map(
          (sr) =>
            sr?.id === id
              ? {
                  ...sr,
                  analysis: { ...sr.analysis, scoreStatus },
                }
              : sr,
        );
      }
    },
    updateAllConversationsMergedAudioLink: (state, action) => {
      const { id, audioLink } = action.payload;
      if (state.allConversationsMain.conversations) {
        state.allConversationsMain.conversations = state.allConversationsMain.conversations.map(
          (cs) =>
            cs?.id === id
              ? {
                  ...cs,
                  customFields: { ...cs.customFields, audioLink },
                }
              : cs,
        );
      }
    },
    setConversationDetailsById: (state, action) => {
      const { conversationId, data } = action.payload;
      state.conversationDetailsById.fetching = false;
      state.conversationDetailsById.fetchingSuccessful = true;
      state.conversationDetailsById.fetchingFailed = false;
      state.conversationDetailsById.error = false;
      state.conversationDetailsById.errorText = '';
      state.conversationDetailsById.details = {
        ...state.conversationDetailsById.details,
        [conversationId]: data,
      };
    },
    resetConversationById: (state) => {
      state.conversationDetailsById = {
        fetching: null,
        fetchingSuccessful: null,
        fetchingFailed: null,
        error: null,
        errorText: null,
        details: null,
      };
    },
    updateConversationMessages: (state, action) => {
      const { conversationId, data } = action.payload;
      if (state.conversationDetailsById.details) {
        state.conversationDetailsById.details[conversationId] = {
          ...(state.conversationDetailsById.details[conversationId] || {}),
          messages: [
            ...(state.conversationDetailsById.details[conversationId]
              ?.messages || []),
            ...data,
          ],
        };
      }
    },
    updateConversationMessageById: (state, action) => {
      const { conversationId, msgId, data } = action.payload;
      if (
        state.conversationDetailsById.details &&
        state.conversationDetailsById.details[conversationId]
      ) {
        const conversation =
          state.conversationDetailsById.details[conversationId];
        conversation.messages =
          conversation.messages?.map((m) =>
            m?.id === msgId
              ? { ...m, gptMessage: { ...m?.gptMessage, ...data } }
              : m,
          ) || [];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(streamChunkReceived, (state, action) => {
        state.streaming.content += action.payload;
      })
      .addCase(streamThinkingReceived, (state, action) => {
        state.streaming.thinkingMessage = action.payload;
      })
      .addCase(streamRequestIdReceived, (state, action) => {
        state.streaming.requestId = action.payload;
      })
      .addCase(streamMessageResponse.pending, (state) => {
        state.streaming.active = true;
        state.streaming.content = '';
        state.streaming.thinkingMessage = '';
        state.streaming.requestId = null;
      })
      .addCase(streamMessageResponse.fulfilled, (state) => {
        state.streaming.active = false;
        state.streaming.thinkingMessage = '';
        state.streaming.requestId = null;
        // content is intentionally kept until next pending so the final
        // rendered text stays visible while React commits the new message
        // into currentConversation; it is cleared by the next pending case.
      })
      .addCase(streamMessageResponse.rejected, (state) => {
        state.streaming.active = false;
        state.streaming.content = '';
        state.streaming.thinkingMessage = '';
        state.streaming.requestId = null;
      })
      .addCase(fetchConversationsMain.pending, (state, action) => {
        const { lastSeen } = action.meta.arg;
        if (lastSeen) {
          state.allConversationsMain.fetching = false;
          state.allConversationsMain.fetchingPagination = true;
        } else {
          state.allConversationsMain.fetching = true;
          state.allConversationsMain.fetchingPagination = false;
        }
      })
      .addCase(fetchConversationsMain.fulfilled, (state, action) => {
        const { data, lastSeenArg, responseDataLastSeen } = action.payload;
        state.allConversationsMain.fetching = false;
        state.allConversationsMain.fetchingPagination = false;
        state.allConversationsMain.fetchingSuccessful = true;
        state.allConversationsMain.fetchingFailed = false;
        state.allConversationsMain.error = false;
        state.allConversationsMain.errorText = '';

        if (lastSeenArg) {
          state.allConversationsMain.conversations = Array.isArray(data)
            ? state.allConversationsMain.conversations
              ? state.allConversationsMain.conversations.concat(data)
              : data
            : state.allConversationsMain.conversations;

          state.allConversationsMain.emptyList =
            Array.isArray(data) && (data.length === 0 || data.length % 10 > 0);
        } else {
          state.allConversationsMain.conversations = data;
          state.allConversationsMain.emptyList =
            Array.isArray(data) && (data.length === 0 || data.length % 10 > 0);
        }

        state.allConversationsMain.lastSeen =
          responseDataLastSeen || state.allConversationsMain.lastSeen;
      })
      .addCase(fetchConversationsMain.rejected, (state, action) => {
        const { lastSeen } = action.meta.arg;
        state.allConversationsMain.fetchingPagination = false;
        if (!lastSeen) {
          state.allConversationsMain.fetching = false;
          state.allConversationsMain.fetchingSuccessful = false;
          state.allConversationsMain.fetchingFailed = true;
          state.allConversationsMain.error = true;
          state.allConversationsMain.errorText = action.payload || 'Failed';
          state.allConversationsMain.conversations = [];
          state.allConversationsMain.emptyList = false;
          state.allConversationsMain.lastSeen = '';
        }
      })
      .addCase(fetchConversationById.pending, (state) => {
        state.conversationDetailsById.fetching = true;
      })
      .addCase(fetchConversationById.fulfilled, (state, action) => {
        const { conversationId, data, setmanually } = action.payload;
        state.conversationDetailsById.fetching = false;
        state.conversationDetailsById.fetchingSuccessful = true;
        state.conversationDetailsById.fetchingFailed = false;
        state.conversationDetailsById.error = false;
        state.conversationDetailsById.errorText = '';
        if (!setmanually) {
          state.conversationDetailsById.details = {
            ...state.conversationDetailsById.details,
            [conversationId]: data,
          };
        }
      })
      .addCase(fetchConversationById.rejected, (state, action) => {
        state.conversationDetailsById.fetching = false;
        state.conversationDetailsById.fetchingFailed = true;
        state.conversationDetailsById.error = true;
        state.conversationDetailsById.errorText = action.payload || 'Failed';
        state.conversationDetailsById.details = null;
      })
      .addCase(getMessageResponse.pending, (state) => {
        state.messageResponse.fetching = true;
        state.messageResponse.fetchingSuccessful = false;
        state.messageResponse.fetchingFailed = false;
        state.messageResponse.error = null;
        state.messageResponse.response = null;
      })
      .addCase(getMessageResponse.fulfilled, (state, action) => {
        state.messageResponse.fetching = false;
        state.messageResponse.fetchingSuccessful = true;
        state.messageResponse.fetchingFailed = false;
        state.messageResponse.error = null;
        state.messageResponse.response = action.payload;
      })
      .addCase(getMessageResponse.rejected, (state, action) => {
        state.messageResponse.fetching = false;
        state.messageResponse.fetchingSuccessful = false;
        state.messageResponse.fetchingFailed = true;
        state.messageResponse.error = action.payload;
        state.messageResponse.response = null;
      })
      .addCase(getMessageResponseAsync.pending, (state) => {
        state.messageResponseAsync.fetching = true;
        state.messageResponseAsync.fetchingSuccessful = false;
        state.messageResponseAsync.fetchingFailed = false;
        state.messageResponseAsync.error = null;
        state.messageResponseAsync.response = null;
      })
      .addCase(getMessageResponseAsync.fulfilled, (state, action) => {
        state.messageResponseAsync.fetching = false;
        state.messageResponseAsync.fetchingSuccessful = true;
        state.messageResponseAsync.fetchingFailed = false;
        state.messageResponseAsync.error = null;
        state.messageResponseAsync.response = action.payload;
      })
      .addCase(getMessageResponseAsync.rejected, (state, action) => {
        state.messageResponseAsync.fetching = false;
        state.messageResponseAsync.fetchingSuccessful = false;
        state.messageResponseAsync.fetchingFailed = true;
        state.messageResponseAsync.error = action.payload;
        state.messageResponseAsync.response = null;
      });

    // --- Candidate thunks ---
    const addM = (t, k) =>
      builder
        .addCase(t.pending, (s) => {
          s[k].processing = true;
          s[k].done = false;
          s[k].failed = false;
        })
        .addCase(t.fulfilled, (s) => {
          s[k].processing = false;
          s[k].done = true;
          s[k].failed = false;
        })
        .addCase(t.rejected, (s, a) => {
          s[k].processing = false;
          s[k].done = false;
          s[k].failed = true;
          s[k].error = a.payload;
        });
    const addF = (t, k) =>
      builder
        .addCase(t.pending, (s) => {
          s[k].fetching = true;
          s[k].fetchingSuccessful = false;
          s[k].fetchingFailed = false;
        })
        .addCase(t.fulfilled, (s, a) => {
          s[k].fetching = false;
          s[k].fetchingSuccessful = true;
          s[k].fetchingFailed = false;
          s[k].data = a.payload;
        })
        .addCase(t.rejected, (s) => {
          s[k].fetching = false;
          s[k].fetchingSuccessful = false;
          s[k].fetchingFailed = true;
        });

    addM(createNewConversationThunk, 'createConversation');
    addM(deleteConversationThunk, 'deleteConversation');
    addF(fetchAllConversations, 'allConversations');
    addF(fetchConversationsByUserId, 'conversationsByUserId');
    addM(updateConversationStatusThunk, 'updateConversationStatusState');
  },
});

export const {
  updateAllConversationsAfterDelete,
  updateConversationsAfterAnalysis,
  updateConversationByKey,
  updatePracticeCallAnalysisScores,
  updatePracticeCallAnalysisScoreStatus,
  updateAllConversationsMergedAudioLink,
  setConversationDetailsById,
  resetConversationById,
  updateConversationMessages,
  updateConversationMessageById,
} = conversationsSlice.actions;

export default conversationsSlice.reducer;
